import React, { useEffect, useRef } from "react";
import {
  Animated,
  Easing,
  StyleSheet,
  TouchableOpacity,
  View,
  type ViewStyle,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Image } from "expo-image";
import Svg, { Circle, Defs, LinearGradient, Stop } from "react-native-svg";

import { type UserImageMedia } from "@/schemas/user/image";

interface AccountAvatarProps {
  avatar: UserImageMedia | null;
  size: number;
  isUploading: boolean;
  completionPercentage: number;
  onUploadPress: () => void;
}

function AccountAvatarImage({
  avatar,
  size,
  isUploading,
  completionPercentage,
  onUploadPress,
}: AccountAvatarProps): React.JSX.Element {
  const strokeWidth = 2;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset =
    circumference - (completionPercentage / 100) * circumference;

  const containerStyle: ViewStyle = {
    height: size,
    width: size,
  };

  const isComplete = completionPercentage === 100; // TODO: this is unused, remove??
  const spinAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (isUploading) {
      Animated.loop(
        Animated.timing(spinAnim, {
          toValue: 1,
          duration: 1200,
          easing: Easing.linear,
          useNativeDriver: true,
        }),
      ).start();
    } else {
      spinAnim.stopAnimation();
      spinAnim.setValue(0);
    }
  }, [isUploading, spinAnim]);

  const spin = spinAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ["0deg", "360deg"],
  });

  return (
    <View style={[styles.container, containerStyle]}>
      <Animated.View
        style={[
          StyleSheet.absoluteFill,
          { transform: [{ rotate: isUploading ? spin : "0deg" }] },
        ]}
        pointerEvents="none"
      >
        <Svg height={size} width={size}>
          <Defs>
            <LinearGradient id="grad" x1="0" y1="0" x2="1" y2="1">
              <Stop offset="0" stopColor="#FF6B6B" stopOpacity="1" />
              <Stop offset="1" stopColor="#4ECDC4" stopOpacity="1" />
            </LinearGradient>
          </Defs>
          <Circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="#E0E0E0"
            strokeWidth={strokeWidth}
            fill="none"
          />
          <Circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={isComplete ? "url(#grad)" : "#4CAF50"}
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
          />
        </Svg>
      </Animated.View>
      <View style={styles.avatarContainer}>
        {isUploading ? (
          <MaterialCommunityIcons
            name="cloud-upload"
            size={size / 2}
            color="#cccccc"
          />
        ) : avatar?.attributes.previewUrl ? (
          <Image
            source={{ uri: avatar.attributes.previewUrl }}
            cachePolicy="memory-disk"
            style={[
              styles.image,
              {
                height: size - 4,
                width: size - 4,
                borderRadius: (size - 4) / 2,
              },
            ]}
          />
        ) : (
          <View
            style={[
              styles.defaultAvatar,
              {
                height: size - 4,
                width: size - 4,
                borderRadius: (size - 4) / 2,
              },
            ]}
          >
            <MaterialCommunityIcons
              name="face-man-outline"
              size={size - 8}
              color="#ffffff"
            />
          </View>
        )}
      </View>
      {avatar === null && (
        <TouchableOpacity style={styles.uploadButton} onPress={onUploadPress}>
          <MaterialCommunityIcons name="camera" size={18} color="#FFFFFF" />
        </TouchableOpacity>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: "relative",
    justifyContent: "center",
    alignItems: "center",
  },
  avatarContainer: {
    position: "absolute",
    justifyContent: "center",
    alignItems: "center",
  },
  defaultAvatar: {
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#cccccc",
  },
  image: {
    backgroundColor: "white",
  },
  uploadButton: {
    position: "absolute",
    right: 0,
    bottom: "10%",
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    borderRadius: 12,
    padding: 4,
  },
});

export default AccountAvatarImage;
